import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api, Me } from './api';

interface AuthState {
  me: Me | null;
  ready: boolean;
  signIn: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Phase 8. Records a login-email change the server has already committed.
   *
   * Only the cached display copy — `me` is never a credential — but it has to
   * be written through to localStorage as well, or a reload restores the old
   * address and the account page tells the user their change did not stick.
   */
  setEmail: (email: string) => void;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // The access token lives in memory, so a reload starts tokenless. The
    // refresh cookie is the only thing that survives — use it to restore.
    void (async () => {
      const ok = await api.bootstrap();
      if (ok) {
        try {
          // /catalog is the cheapest authenticated probe that also tells us
          // whether the licence still permits access.
          await api.catalog();
          const stored = localStorage.getItem('me');
          if (stored) setMe(JSON.parse(stored) as Me);
        } catch {
          localStorage.removeItem('me');
        }
      }
      setReady(true);
    })();
  }, []);

  const signIn = async (email: string, password: string, turnstileToken?: string) => {
    const user = await api.login(email, password, turnstileToken);
    localStorage.setItem('me', JSON.stringify(user)); // display only, never a credential
    setMe(user);
  };

  const signOut = async () => {
    localStorage.removeItem('me');
    setMe(null);
    await api.logout();
  };

  const setEmail = (email: string) => {
    setMe((prev) => {
      if (!prev) return prev;
      const next = { ...prev, email };
      localStorage.setItem('me', JSON.stringify(next));
      return next;
    });
  };

  return (
    <Ctx.Provider value={{ me, ready, signIn, signOut, setEmail }}>{children}</Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
